
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";
import { validateSlackSettings } from "../utils/slackValidation";
import { useSlackStorage } from "./useSlackStorage";

/**
 * Hook for managing Slack connection operations
 */
export const useConnectionManager = (
  aiStaffId: string,
  setIsConnected: (connected: boolean) => void,
  setConnectionError: (error: string | null) => void,
  setIsLoading: (loading: boolean) => void
) => {
  const { toast } = useToast();
  const { saveIntegration, deleteIntegration } = useSlackStorage(aiStaffId);
  
  const handleConnect = async (
    webhookUrl: string,
    slackToken: string,
    saveToDatabase: boolean = true,
    triggerTag: string = "@marketing",
    isFileProcessingEnabled: boolean = true,
    isAutoResponsesEnabled: boolean = true
  ) => {
    setIsLoading(true);
    setConnectionError(null);
    
    try {
      logger.info({
        message: "Connecting to Slack",
        context: { webhookUrl: webhookUrl.substring(0, 15) + '...', aiStaffId }
      });
      
      // Validate inputs first
      const validationError = validateSlackSettings(webhookUrl, slackToken);
      if (validationError) {
        setConnectionError(validationError);
        toast({
          title: "Validation Error",
          description: validationError,
          variant: "destructive",
        });
        return false;
      }
      
      // Save to database if saveToDatabase flag is true
      if (saveToDatabase) {
        try {
          logger.info({
            message: "Saving integration to database",
            context: { aiStaffId, triggerTag }
          });
          
          await saveIntegration(
            webhookUrl, 
            slackToken, 
            triggerTag,
            isFileProcessingEnabled,
            isAutoResponsesEnabled
          );
          
          logger.info({
            message: "Successfully saved integration",
            context: { aiStaffId }
          });
        } catch (saveError) {
          logger.error({
            message: "Error saving integration",
            error: saveError instanceof Error ? saveError : new Error(String(saveError)),
            context: { aiStaffId }
          });
          
          const errorMessage = saveError instanceof Error 
            ? saveError.message 
            : "Failed to save integration to database";
          
          setConnectionError(errorMessage);
          toast({
            title: "Database Error",
            description: errorMessage,
            variant: "destructive",
          });
          
          return false;
        }
      }
      
      setIsConnected(true);
      
      toast({
        title: "Connected to Slack",
        description: "Your Slack integration has been set up successfully",
      });
      
      return true;
    } catch (error) {
      logger.error({
        message: "Error connecting to Slack",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Unknown error connecting to Slack";
      
      setConnectionError(errorMessage);
      setIsConnected(false);
      
      toast({
        title: "Connection failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDisconnect = async () => {
    setIsLoading(true);
    
    try {
      logger.info({
        message: "Disconnecting from Slack",
        context: { aiStaffId }
      });
      
      const success = await deleteIntegration();
      
      if (!success) {
        throw new Error("Failed to delete integration");
      }
      
      setIsConnected(false);
      
      toast({
        title: "Disconnected from Slack",
        description: "Your Slack integration has been removed",
      });
      
      return true;
    } catch (error) {
      logger.error({
        message: "Error disconnecting from Slack",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { aiStaffId }
      });
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Unknown error disconnecting from Slack";
      
      toast({
        title: "Disconnection failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    handleConnect,
    handleDisconnect
  };
};
